import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from '../utils/axiosInstance';

// PIM JOIN — Pitfall 4: PIM is keyed by BARE numeric `parent_id`, not the
// full GID. Use this to convert before lookup. Local copy; consumer code
// (ProductResources.jsx) has its own copy per the inline-duplication
// invariant — keep them in sync.
const gidLeaf = (gid) => (gid ? gid.split('/').pop() : '');

// Per-locale status rollup priority — worst wins. `failed` > `pending` >
// `pushed` > none. Diverges from Phase 20's pivot (which has pending=2 >
// failed=1 — see Pitfall 2); for the LIST rollup we surface `failed` so
// operators see the worst case across a product's fields and don't miss
// failures hidden behind a `pending` field on the same product.
const STATUS_RANK = { failed: 4, pending: 3, pushed: 2, null: 1, undefined: 0 };
function rollupStatus(values) {
  let worst = null;
  let worstRank = 0;
  for (const v of values) {
    const r = STATUS_RANK[v ?? 'null'] ?? 0;
    if (r > worstRank) { worst = v; worstRank = r; }
  }
  return worst;
}

// PRODUCT-only rows are ~13.7K (one per product per field_key, ~6 fields each
// → ~82K rows). Loading 5000 in one shot pulls the per-locale status-pivot CTE
// + option_label join across all of them, which takes 30–90s in production.
// 100 keeps each page render snappy; the prev/next pagination UI walks
// through pages. Lazy-load expand uses 5000 separately — that's per-parent
// (~50 rows max), comfortable headroom.
const DEFAULT_PAGE_SIZE = 100;

/**
 * Hook for the Phase 21 Products page (new view, post-cutover).
 *
 * Clone of `useContentResources.js` (Phase 18) with these documented
 * divergences (D-05, D-07, D-08, D-12, D-15):
 *
 *   1. NO `resourceType` arg — single-page entry; the `resourceTypeFilter`
 *      chip is internal state instead.
 *   2. `fetchData` does THREE parallel fetches (D-07): product-resources
 *      list + sync-status + PIM `/api/transly/products/`. PIM is merged
 *      client-side keyed by BARE `parent_id` — Pitfall 4 mandates
 *      `gidLeaf(row.parent_product_id)` lookup at consumer sites.
 *   3. NEW `fetchChildren(productGid)` action for D-05 lazy-load expand
 *      using Wave 1's D-14 `?parent_product_id=` filter; result cached in
 *      `childrenByProduct[productGid]`. Concurrent fetches for the same
 *      product are deduped via `loadingChildren` Set.
 *   4. DROP the field-key and has-html filter state from useContentResources
 *      (D-08 — products have only 6 field-key values; low filter value vs
 *      UI noise). ADD `resourceTypeFilter` (default 'PRODUCT'), `vendor`,
 *      `localeFilter`, and PIM JOIN state (`pimByParentId`, `pimVendors`,
 *      `pimLocaleStatuses`).
 *
 * Status filter (D-15) uses Phase 20 vocabulary
 * (`pushed | pending | failed | missing`) wired to the per-row `de_status`
 * / `es_status` columns — NOT the legacy PIM `translated/partial/outdated`
 * vocab.
 *
 * All other behaviour (300ms search debounce, optimistic policy PATCH with
 * rollback, reset-to-page-1 on filter changes, sync trigger + last-synced
 * timestamp) mirrors `useContentResources.js` verbatim — only the URLs
 * and the divergences above differ.
 *
 * Filters are SERVER-SIDE: every filter change refetches with query params,
 * matching Phase 20's read API.
 */
export default function useProductResources() {
  // Data
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // D-08 filter chips on the new view
  const [resourceTypeFilter, setResourceTypeFilter] = useState('PRODUCT'); // 'PRODUCT' | 'PRODUCT_OPTION' | 'PRODUCT_OPTION_VALUE' | ''
  const [policy, setPolicy] = useState('all');                              // 'all' | 'translate' | 'skip' | 'auto-skipped'
  const [status, setStatus] = useState('all');                              // D-15 Phase 20 vocab: 'all' | 'pushed' | 'pending' | 'failed' | 'missing'
  const [vendor, setVendor] = useState('');                                 // PIM vendor dropdown
  const [localeFilter, setLocaleFilter] = useState('');                     // '' | 'de' | 'es' (per-locale status filter)
  // PIM product state — backend ProductListView accepts `?status=ACTIVE|DRAFT|ARCHIVED`
  // and defaults to ACTIVE if absent. We mirror that default here. Set to ''
  // to bypass the filter (show all states).
  const [productState, setProductState] = useState('ACTIVE');

  // Search
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);

  // Sync
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);

  // D-05 lazy-load expand state — keyed by full GID `gid://shopify/Product/<id>`
  const [childrenByProduct, setChildrenByProduct] = useState({});
  const [loadingChildren, setLoadingChildren] = useState(() => new Set());

  // D-07 PIM JOIN state — Pitfall 4: `pimByParentId` is keyed by BARE
  // `parent_id` (numeric Shopify ID), NOT the full GID. Consumers must use
  // `gidLeaf(row.parent_product_id)` to convert before lookup.
  const [pimByParentId, setPimByParentId] = useState({});
  const [pimVendors, setPimVendors] = useState([]);
  const [pimLocaleStatuses, setPimLocaleStatuses] = useState({});

  // Debounce search input (300ms — matches Phase 14 / Phase 15 / Phase 18)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: pageSize };
      if (resourceTypeFilter) params.resource_type = resourceTypeFilter;
      if (policy && policy !== 'all') params.policy = policy;
      if (status && status !== 'all') params.status = status;       // D-15 Phase 20 vocab
      if (debouncedQ) params.q = debouncedQ;
      // List = one row per product. PRODUCT rows come ~6-per-product
      // (UNIQUE(resource_id, field_key)) — filter to the title field so each
      // result row IS a product. The drill-in fetches all fields per product
      // separately. For OPTION / OPTION_VALUE filters we pull all rows
      // (groupedRows clusters them by parent_product_id client-side).
      if (resourceTypeFilter === 'PRODUCT') params.field_key = 'title';
      // Sort by Shopify GID descending — Shopify assigns GIDs monotonically
      // so this surfaces newest products first, matching Shopify Admin's
      // default. Safe to use now that the merge fetches PIM by explicit
      // parent_ids (sequential step below) so the two sort orders no longer
      // need to align.
      params.order_by = 'parent_product_id_desc';
      // Server-side product-state filter (Phase 21). Denormalized from PIM
      // into product_resources.p_status at sync time, so the LIST query
      // filters AND counts exactly — pagination is now precise (a page is
      // genuinely 100 ACTIVE products, not "100 mixed-state rows of which
      // ~78 happen to be ACTIVE"). Empty productState = no filter.
      if (productState) params.p_status = productState;

      // STEP 1 — Fetch the page list + sync status in parallel.
      const [listRes, syncRes] = await Promise.all([
        axios.get('/api/transly/product-resources/', { params }),
        axios.get('/api/transly/product-resources/sync-status/'),
      ]);
      const listRows = listRes.data.rows || [];
      setRows(listRows);
      setTotal(listRes.data.total || 0);
      setLastSynced(syncRes.data.last_completed || null);

      // STEP 2 — Look up PIM data for ONLY the products on this page.
      // Adds ~100ms over the previous parallel-fetch shape but the merge
      // is now exact: every visible row gets its PIM enrichment, the
      // state filter is correct rather than best-effort, and the two
      // sources can use different sort orders without breaking overlap.
      // Backend caps parent_ids at 250; pageSize=100 fits comfortably.
      const pageGids = [...new Set(listRows.map((r) => gidLeaf(r.parent_product_id)).filter(Boolean))];
      const pimParams = {};
      if (pageGids.length > 0) {
        pimParams.parent_ids = pageGids.join(',');
      } else if (productState) {
        // Empty list — fetch a small PIM page anyway so the vendor
        // dropdown stays populated. Status filter narrows the load.
        pimParams.status = productState;
        pimParams.pageSize = 200;
      }
      const pimRes = await axios.get('/api/transly/products/', { params: pimParams });

      // D-07 PIM JOIN merge — Pitfall 4: PIM keyed by BARE `parent_id`
      // (numeric), Phase 20 by full GID. Components must use
      // `gidLeaf(row.parent_product_id)` to look up PIM rows for a given
      // product-resource row.
      const pim = pimRes.data || {};
      const map = {};
      for (const p of pim.products || []) map[p.parent_id] = p;
      setPimByParentId(map);
      setPimVendors(pim.vendors || []);
      setPimLocaleStatuses(pim.localeStatuses || {});
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load product resources');
    } finally {
      setLoading(false);
    }
  }, [resourceTypeFilter, page, pageSize, policy, status, debouncedQ, vendor, productState]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Group rows by parent_product_id so the LIST renders one row per product
  // (instead of one row per product field). The `product_resources` table
  // is keyed `UNIQUE(resource_id, field_key)` — for a single PRODUCT this
  // produces ~6 rows (title, body_html, handle, seo_title, seo_description,
  // +1 more), all with the same `parent_product_id`. The expand chevron
  // separately fetches OPTION + OPTION_VALUE rows via fetchChildren().
  //
  // Each group exposes:
  //   - parent_product_id: the GID
  //   - displayRow: representative row (prefer the title-field row, else first)
  //   - fieldRows: all rows in the group
  //   - statusRollup: { de: 'failed' | 'pending' | 'pushed' | null, es: ... }
  //     — per-locale worst status across the product's fields
  //   - policyRollup: 'auto' | 'translate' | 'skip' | 'mixed' — if all
  //     fields share a single non-null policy, show it; if all are null
  //     (auto), show 'auto'; otherwise 'mixed'.
  const groupedRows = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    const byProduct = new Map();
    for (const row of rows) {
      const pid = row.parent_product_id;
      if (!byProduct.has(pid)) byProduct.set(pid, []);
      byProduct.get(pid).push(row);
    }
    // Discover per-locale status keys dynamically from the first row
    // (e.g. `de_status`, `es_status` — set by Phase 20's _attach_locale_status).
    const sample = rows[0] || {};
    const localeKeys = Object.keys(sample).filter((k) => k.endsWith('_status'));

    const groups = [];
    for (const [pid, fieldRows] of byProduct) {
      // Pick the title-field row as the representative (it carries `title`);
      // fall back to first row if no title field is present in this page.
      const displayRow =
        fieldRows.find((r) => r.field_key === 'title') || fieldRows[0];

      // Rollup per-locale status (worst wins across all field rows).
      const statusRollup = {};
      for (const k of localeKeys) {
        statusRollup[k] = rollupStatus(fieldRows.map((r) => r[k]));
      }

      // Rollup policy: collect distinct non-null values.
      const policies = new Set();
      let hasNull = false;
      for (const r of fieldRows) {
        if (r.translation_policy == null) hasNull = true;
        else policies.add(r.translation_policy);
      }
      let policyRollup;
      if (policies.size === 0) policyRollup = 'auto';
      else if (policies.size === 1 && !hasNull) policyRollup = [...policies][0];
      else policyRollup = 'mixed';

      groups.push({
        parent_product_id: pid,
        displayRow,
        fieldRows,
        statusRollup,
        policyRollup,
      });
    }

    // No client-side state filter needed — the LIST query now filters
    // server-side via ?p_status=... (denormalized column) so pagination
    // is exact. PIM is still fetched for vendor / product_type
    // enrichment but isn't load-bearing for the state filter anymore.
    return groups;
  }, [rows]);

  // Reset to page 1 when filters change (avoid stranded pages). Adds
  // `resourceTypeFilter` and `vendor` to the useContentResources deps array.
  useEffect(() => {
    setPage(1);
  }, [resourceTypeFilter, policy, status, debouncedQ, vendor, productState]);

  // D-05 lazy-load expand: fetch a single product's children
  // (PRODUCT_OPTION + PRODUCT_OPTION_VALUE rows for that product) using
  // Wave 1's D-14 `parent_product_id=` filter. Cached in
  // `childrenByProduct[productGid]`; concurrent fetches for the same
  // product GID are deduped via `loadingChildren` Set.
  //
  // Pitfall 3: page_size: 5000 (the _MAX_PAGE_SIZE cap) — pagination is
  // over the GLOBAL filtered query, not per-parent, so we explicitly
  // request the maximum to ensure all options + option_values for one
  // product fit in one response. Realistic scale: ~5 options × ~10 values
  // per product ≈ 55 rows; 5000 is comfortable headroom.
  const fetchChildren = useCallback(async (productGid) => {
    // Dedup concurrent fetches for the same product
    let alreadyLoading = false;
    setLoadingChildren((prev) => {
      if (prev.has(productGid)) {
        alreadyLoading = true;
        return prev;
      }
      const next = new Set(prev);
      next.add(productGid);
      return next;
    });
    if (alreadyLoading) return;

    try {
      const res = await axios.get('/api/transly/product-resources/', {
        params: { parent_product_id: productGid, page_size: 5000 },
      });
      setChildrenByProduct((c) => ({ ...c, [productGid]: res.data.rows || [] }));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load product children');
    } finally {
      setLoadingChildren((prev) => {
        const next = new Set(prev);
        next.delete(productGid);
        return next;
      });
    }
  }, []);

  // Optimistic policy PATCH with rollback — lifted verbatim from
  // useContentResources.js:94-122; only the URL string differs.
  const updatePolicy = useCallback(async (productResourceId, newPolicy) => {
    // Optimistic update — flip the row first, then PATCH; rollback on error.
    const prev = rows;
    setRows((curr) =>
      curr.map((r) =>
        r.id === productResourceId
          ? {
              ...r,
              translation_policy: newPolicy,
              auto_skip_reason: newPolicy === null ? r.auto_skip_reason : null,
            }
          : r,
      ),
    );
    try {
      await axios.patch(`/api/transly/product-resources/${productResourceId}/policy/`, {
        policy: newPolicy,
      });
      // For policy=null (reset to auto), the server re-classified — refetch to
      // learn the new auto_skip_reason.
      if (newPolicy === null) {
        await fetchData();
      }
    } catch (err) {
      // Rollback
      setRows(prev);
      throw err;
    }
  }, [rows, fetchData]);

  const triggerSync = useCallback(async () => {
    await axios.post('/api/transly/product-resources/sync/');
  }, []);

  return {
    // Data
    rows,
    groupedRows,            // one entry per parent_product_id (LIST view)
    total,
    loading,
    error,
    // Filters (D-08)
    resourceTypeFilter,
    setResourceTypeFilter,
    policy,
    setPolicy,
    status,
    setStatus,
    vendor,
    setVendor,
    localeFilter,
    setLocaleFilter,
    productState,
    setProductState,
    q,
    setQ,
    // Pagination
    page,
    setPage,
    pageSize,
    // Sync
    syncModalOpen,
    setSyncModalOpen,
    lastSynced,
    triggerSync,
    // Actions
    updatePolicy,
    fetchData,
    // D-05 lazy-load
    childrenByProduct,
    fetchChildren,
    loadingChildren,
    // D-07 PIM JOIN (Pitfall 4: pimByParentId keyed by BARE parent_id)
    pimByParentId,
    pimVendors,
    pimLocaleStatuses,
  };
}
